import { isString, some } from "lodash";

/**
 * Recovers from post-deploy chunk load failures (CRMDEV-7093).
 *
 * After a deploy, a tab opened before the deploy may lazy-load a chunk whose
 * hashed filename no longer exists on the server ("Loading CSS chunk 967
 * failed"). The fix is a one-time, sessionStorage-guarded page reload so the
 * browser picks up the new asset manifest. If a reload already happened
 * recently (the chunk is genuinely broken, not just stale), the error is left
 * to propagate to the error boundary / Sentry as usual.
 */

const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /loading chunk [^\s]+ failed/i,
  /loading css chunk [^\s]+ failed/i,
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i
];

const STORAGE_KEY = "crm-web.chunk-load-reloaded-at";
const DEFAULT_RELOAD_WINDOW_MS = 60_000;

export function isChunkLoadError(input: unknown): boolean {
  if (!input) {
    return false;
  }

  if (isString(input)) {
    return some(CHUNK_ERROR_PATTERNS, (pattern) => pattern.test(input));
  }

  const error = input as { name?: unknown; message?: unknown };

  if (error.name === "ChunkLoadError") {
    return true;
  }

  return isString(error.message) && isChunkLoadError(error.message);
}

export interface ChunkLoadRecoveryOptions {
  /** Lazily resolved so environments without sessionStorage do not throw. */
  getStorage?: () => Storage;
  reload?: () => void;
  now?: () => number;
  reloadWindowMs?: number;
}

export interface ChunkLoadRecovery {
  /**
   * Reloads the page once per reload window. Returns true when a reload was
   * triggered (the error is being auto-recovered), false when the guard says
   * a reload already happened recently or the guard cannot be persisted.
   */
  attemptRecovery(): boolean;
  /** True when this page load has already triggered a recovery reload. */
  isReloadPending(): boolean;
  /**
   * True when a chunk load error should be reported to Sentry: the guarded
   * reload was already used (or is unavailable), so auto-recovery will not
   * happen and the error must surface.
   */
  shouldReport(): boolean;
  /** Installs window "error" and "unhandledrejection" listeners. */
  install(target: Window): void;
}

export function createChunkLoadRecovery(
  options: ChunkLoadRecoveryOptions = {}
): ChunkLoadRecovery {
  const {
    getStorage = () => window.sessionStorage,
    reload = () => window.location.reload(),
    now = () => Date.now(),
    reloadWindowMs = DEFAULT_RELOAD_WINDOW_MS
  } = options;

  let reloadPending = false;

  const readLastReloadAt = (): number | undefined => {
    try {
      const raw = getStorage().getItem(STORAGE_KEY);
      const parsed = raw === null ? NaN : Number(raw);
      return Number.isFinite(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  };

  const writeLastReloadAt = (timestamp: number): boolean => {
    try {
      getStorage().setItem(STORAGE_KEY, String(timestamp));
      return true;
    } catch {
      return false;
    }
  };

  const isStorageUsable = (): boolean => {
    try {
      const storage = getStorage();
      const probeKey = `${STORAGE_KEY}.probe`;
      storage.setItem(probeKey, "1");
      storage.removeItem(probeKey);
      return true;
    } catch {
      return false;
    }
  };

  const canAttemptRecovery = (): boolean => {
    if (!isStorageUsable()) {
      // Without a persistable guard a genuinely broken chunk would cause an
      // infinite reload loop, so recovery is off the table.
      return false;
    }
    const lastReloadAt = readLastReloadAt();
    return lastReloadAt === undefined || now() - lastReloadAt >= reloadWindowMs;
  };

  const attemptRecovery = (): boolean => {
    if (reloadPending) {
      return true;
    }

    if (!canAttemptRecovery() || !writeLastReloadAt(now())) {
      return false;
    }

    reloadPending = true;
    reload();
    return true;
  };

  const isReloadPending = () => reloadPending;

  const shouldReport = () => !reloadPending && !canAttemptRecovery();

  const install = (target: Window): void => {
    target.addEventListener("error", (event: ErrorEvent) => {
      if (isChunkLoadError(event.error ?? event.message)) {
        attemptRecovery();
      }
    });

    target.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason) && attemptRecovery()) {
        event.preventDefault();
      }
    });
  };

  return { attemptRecovery, isReloadPending, shouldReport, install };
}

const defaultRecovery = createChunkLoadRecovery();

export function installChunkLoadRecovery(): void {
  defaultRecovery.install(window);
}

/**
 * For Sentry beforeSend: returns true when a chunk load error should still be
 * reported (auto-recovery already used or unavailable — the "reload loop"
 * case). Returns false while an auto-recovery reload is pending or possible.
 */
export function shouldReportChunkLoadError(): boolean {
  return defaultRecovery.shouldReport();
}
